/**
 * Near-player precipitation from a stable planet-local cell grid.
 *
 * Particle identity is `(absolute cell A, absolute cell B, ordinal)`, never a slot in a box that
 * follows the viewer. Moving the grid window only changes which absolute cells its immutable slots
 * address; overlapping cells reproduce the same particles. Fall, wind, density, snow flutter, and
 * vertical recycling are evaluated in the vertex shader, so update cost is constant and no particle
 * buffer is uploaded after construction.
 */
import * as THREE from "three";
import type { WeatherColumn } from "@hyperlight/shared";
import { WEATHER_MAP_GLSL, type WeatherField } from "../world/weather/weatherField";
import { WEATHER_SURFACE_GLSL, type WeatherSurface } from "../world/weather/weatherSurface";

export interface FxPose {
  center: THREE.Vector3;
  quat: THREE.Quaternion;
  invQuat: THREE.Quaternion;
}

const GRID_N = 18;
const CELL_SIZE = 6;
const PARTICLES_PER_CELL = 20;
const N = GRID_N * GRID_N * PARTICLES_PER_CELL;
const BOX_R = 26;
const BOX_R_MAX = 48;
const BOX_R_CAM = 0.7;
const CENTER_LERP = 0.42;
const BOX_TOP = 30;
const GROUND_DROP = 2.2;
const MAX_DROP = 80;
const RAIN_FALL = 24;
const SNOW_FALL = 2.4;
const RAIN_STREAK = 1.1;
const WIND_RAIN = 7;
const WIND_SNOW = 1;
const SNOW_FLUTTER = 0.4;

type Uniform<T> = { value: T };

interface PrecipUniforms {
  uCenter: Uniform<THREE.Vector3>;
  uBasisA: Uniform<THREE.Vector3>;
  uBasisB: Uniform<THREE.Vector3>;
  uUp: Uniform<THREE.Vector3>;
  uGridA: Uniform<THREE.Vector3>;
  uGridB: Uniform<THREE.Vector3>;
  uFace: Uniform<THREE.Vector3>;
  uGridBase: Uniform<THREE.Vector2>;
  uWindPath: Uniform<THREE.Vector3>;
  uShellRadius: Uniform<number>;
  uHC: Uniform<number>;
  uBoxR: Uniform<number>;
  uDrop: Uniform<number>;
  uFallDistance: Uniform<number>;
  uIntensity: Uniform<number>;
  uTime: Uniform<number>;
  uFaceSeed: Uniform<number>;
}

const POSITION_GLSL = /* glsl */ `
  #include <common>
  ${WEATHER_SURFACE_GLSL}
  ${WEATHER_MAP_GLSL}
  uniform float uWeatherOn, uCloudBase;
  attribute vec3 aCell;
  uniform vec3 uCenter;
  uniform vec3 uBasisA;
  uniform vec3 uBasisB;
  uniform vec3 uUp;
  uniform vec3 uGridA;
  uniform vec3 uGridB;
  uniform vec3 uFace;
  uniform vec2 uGridBase;
  uniform vec3 uWindPath;
  uniform float uShellRadius;
  uniform float uHC;
  uniform float uBoxR;
  uniform float uDrop;
  uniform float uFallDistance;
  uniform float uIntensity;
  uniform float uTime;
  uniform float uFaceSeed;

  float hash31(vec3 p) {
    return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453123);
  }

  vec4 cellSeed(vec2 cell, float ordinal) {
    vec3 p = vec3(cell, ordinal + uFaceSeed * 31.0);
    return vec4(
      hash31(p),
      hash31(p + vec3(19.19, 7.13, 3.71)),
      hash31(p + vec3(3.17, 29.41, 11.73)),
      hash31(p + vec3(43.11, 5.23, 17.17))
    );
  }

  float wrapRange(float value, float low, float span) {
    return value - span * floor((value - low) / span);
  }

  vec4 precipPosition(float snow) {
    vec2 cell = uGridBase + aCell.xy;
    vec4 seed = cellSeed(cell, aCell.z);
    float coordA = (cell.x + seed.x) * ${CELL_SIZE.toFixed(1)};
    float coordB = (cell.y + seed.y) * ${CELL_SIZE.toFixed(1)};
    float faceCoord = sqrt(max(0.0, uShellRadius * uShellRadius - coordA * coordA - coordB * coordB));
    vec3 surface = uGridA * coordA + uGridB * coordB + uFace * faceCoord;
    vec3 columnUp = normalize(surface);
    float initialH = mix(-uDrop, ${BOX_TOP.toFixed(1)}, seed.z);
    vec3 raw = surface + columnUp * initialH + uWindPath;

    vec3 delta = raw - uCenter;
    float a = dot(delta, uBasisA);
    float b = dot(delta, uBasisB);
    float baseH = dot(delta, uUp) + uHC;
    float fallMul = mix(1.0, 0.75 + 0.5 * seed.w, snow);
    float verticalSpan = ${BOX_TOP.toFixed(1)} + uDrop;
    float h = wrapRange(baseH - uFallDistance * fallMul, -uDrop, verticalSpan);
    vec3 localPosition = raw + uUp * (h - baseH);

    if (snow > 0.5) {
      float flutterPhase = seed.w * 6.2831853 + uTime * (1.1 + seed.w * 0.6);
      localPosition += (uBasisA * sin(flutterPhase) + uBasisB * cos(flutterPhase * 0.73)) * ${SNOW_FLUTTER.toFixed(1)};
    }

    float intensity = uIntensity;
    float exposure = 1.0;
    if (uWeatherOn > 0.5) {
      vec4 weather = weatherMap(localPosition);
      intensity = min(intensity, weather.g * mix(1.0-weather.a, weather.a, snow));
      vec2 suv = wxSurfaceUv(localPosition);
      float stop = texture2D(uWxSurface, suv).r;
      float altitude = length(localPosition)-uWxRadius;
      exposure = wxSurfaceInside(suv) * step(stop+0.08, altitude);
      exposure *= 1.0-smoothstep(uCloudBase, uCloudBase+35.0, altitude);
    }
    float inside = step(abs(a), uBoxR) * step(abs(b), uBoxR) * step(seed.w, intensity) * exposure;
    return vec4(localPosition, inside);
  }
`;

const RAIN_VERTEX = /* glsl */ `
  ${POSITION_GLSL}
  attribute float aEnd;
  uniform vec3 uStreak;
  #include <logdepthbuf_pars_vertex>
  void main() {
    vec4 particle = precipPosition(0.0);
    vec3 transformed = particle.xyz + uStreak * aEnd;
    gl_Position = particle.w > 0.5
      ? projectionMatrix * modelViewMatrix * vec4(transformed, 1.0)
      : vec4(2.0, 2.0, 2.0, 1.0);
    #include <logdepthbuf_vertex>
  }
`;

const RAIN_FRAGMENT = /* glsl */ `
  uniform vec3 uColor;
  uniform float uOpacity;
  #include <logdepthbuf_pars_fragment>
  void main() {
    gl_FragColor = vec4(uColor, uOpacity);
    #include <logdepthbuf_fragment>
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

const SNOW_VERTEX = /* glsl */ `
  ${POSITION_GLSL}
  uniform float uPointSize;
  uniform float uPointScale;
  #include <logdepthbuf_pars_vertex>
  void main() {
    vec4 particle = precipPosition(1.0);
    vec4 mvPosition = modelViewMatrix * vec4(particle.xyz, 1.0);
    gl_Position = particle.w > 0.5 ? projectionMatrix * mvPosition : vec4(2.0, 2.0, 2.0, 1.0);
    gl_PointSize = particle.w > 0.5
      ? max(1.0, uPointSize * uPointScale / max(1.0, -mvPosition.z))
      : 0.0;
    #include <logdepthbuf_vertex>
  }
`;

const SNOW_FRAGMENT = /* glsl */ `
  uniform sampler2D uMap;
  uniform vec3 uColor;
  uniform float uOpacity;
  #include <logdepthbuf_pars_fragment>
  void main() {
    vec4 flake = texture2D(uMap, gl_PointCoord);
    if (flake.a < 0.01) discard;
    gl_FragColor = vec4(uColor * flake.rgb, uOpacity * flake.a);
    #include <logdepthbuf_fragment>
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

function fract(value: number): number {
  return value - Math.floor(value);
}

function hash31(x: number, y: number, z: number): number {
  return fract(Math.sin(x * 127.1 + y * 311.7 + z * 74.7) * 43758.5453123);
}

function flakeTexture(): THREE.Texture {
  const c = document.createElement("canvas");
  c.width = c.height = 32;
  const g = c.getContext("2d")!;
  const grad = g.createRadialGradient(16, 16, 0, 16, 16, 16);
  grad.addColorStop(0, "rgba(255,255,255,1)");
  grad.addColorStop(0.5, "rgba(255,255,255,0.7)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = grad;
  g.fillRect(0, 0, 32, 32);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function commonUniforms(): PrecipUniforms {
  return {
    uCenter: { value: new THREE.Vector3() },
    uBasisA: { value: new THREE.Vector3(1, 0, 0) },
    uBasisB: { value: new THREE.Vector3(0, 0, 1) },
    uUp: { value: new THREE.Vector3(0, 1, 0) },
    uGridA: { value: new THREE.Vector3(1, 0, 0) },
    uGridB: { value: new THREE.Vector3(0, 0, 1) },
    uFace: { value: new THREE.Vector3(0, 1, 0) },
    uGridBase: { value: new THREE.Vector2() },
    uWindPath: { value: new THREE.Vector3() },
    uShellRadius: { value: 1 },
    uHC: { value: 0 },
    uBoxR: { value: BOX_R },
    uDrop: { value: GROUND_DROP },
    uFallDistance: { value: 0 },
    uIntensity: { value: 0 },
    uTime: { value: 0 },
    uFaceSeed: { value: 0 },
  };
}

function staticGeometry(rain: boolean): { geometry: THREE.BufferGeometry; cells: Float32Array } {
  const verticesPerParticle = rain ? 2 : 1;
  const cells = new Float32Array(N * 3);
  const expandedCells = new Float32Array(N * verticesPerParticle * 3);
  const position = new Float32Array(N * verticesPerParticle * 3);
  const ends = rain ? new Float32Array(N * 2) : null;
  let particle = 0;
  for (let y = 0; y < GRID_N; y++) {
    for (let x = 0; x < GRID_N; x++) {
      for (let ordinal = 0; ordinal < PARTICLES_PER_CELL; ordinal++, particle++) {
        const o = particle * 3;
        cells[o] = x;
        cells[o + 1] = y;
        cells[o + 2] = ordinal;
        for (let vertex = 0; vertex < verticesPerParticle; vertex++) {
          expandedCells.set(cells.subarray(o, o + 3), (particle * verticesPerParticle + vertex) * 3);
        }
        if (ends) ends[particle * 2 + 1] = 1;
      }
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(position, 3));
  geometry.setAttribute("aCell", new THREE.BufferAttribute(expandedCells, 3));
  if (ends) geometry.setAttribute("aEnd", new THREE.BufferAttribute(ends, 1));
  geometry.setDrawRange(0, N * verticesPerParticle);
  return { geometry, cells };
}

export class PrecipFx {
  private readonly snowPartner: PrecipFx | null;
  private readonly mixedWeather: WeatherColumn = { cloud: 0, precip: 0, kind: 0, windx: 0, windy: 0, windz: 0, windSpeed: 0 };
  private readonly rain: THREE.LineSegments;
  private readonly snow: THREE.Points;
  private readonly rainMaterial: THREE.ShaderMaterial;
  private readonly snowMaterial: THREE.ShaderMaterial;
  private readonly uniforms = commonUniforms();
  private readonly cells: Float32Array;
  private readonly snowMap: THREE.Texture;
  private wasActive = false;
  private activeKind = 0;
  private intensity = 0;
  private lastKind = 1;
  private clock = 0;
  private faceAxis = -1;
  private faceSign = 1;

  private readonly _pLocal = new THREE.Vector3();
  private readonly _lastPlayerLocal = new THREE.Vector3();
  private readonly _cLocal = new THREE.Vector3();
  private readonly _sourceLocal = new THREE.Vector3();
  private readonly _upLocal = new THREE.Vector3();
  private readonly _windLocal = new THREE.Vector3();
  private readonly _vel = new THREE.Vector3();
  private readonly _tanA = new THREE.Vector3();
  private readonly _tanB = new THREE.Vector3();
  private readonly _streak = new THREE.Vector3();
  private readonly _sampleUp = new THREE.Vector3();

  constructor(scene: THREE.Scene, mixedChild = false) {
    this.snowPartner = mixedChild ? null : new PrecipFx(scene, true);
    const rainData = staticGeometry(true);
    const snowData = staticGeometry(false);
    this.cells = snowData.cells;
    this.rainMaterial = new THREE.ShaderMaterial({
      uniforms: {
        ...this.uniforms,
        uWeatherOn: { value: 0 }, uCloudBase: { value: 1e9 },
        uStreak: { value: new THREE.Vector3(0, -RAIN_STREAK, 0) },
        uColor: { value: new THREE.Color(0xacc4dd) },
        uOpacity: { value: 0.5 },
      },
      vertexShader: RAIN_VERTEX,
      fragmentShader: RAIN_FRAGMENT,
      transparent: true,
      depthWrite: false,
      fog: false,
    });
    this.rain = new THREE.LineSegments(rainData.geometry, this.rainMaterial);
    this.rain.frustumCulled = false;
    this.rain.renderOrder = 6;
    this.rain.visible = false;

    this.snowMap = flakeTexture();
    this.snowMaterial = new THREE.ShaderMaterial({
      uniforms: {
        ...this.uniforms,
        uWeatherOn: { value: 0 }, uCloudBase: { value: 1e9 },
        uMap: { value: this.snowMap },
        uColor: { value: new THREE.Color(0xffffff) },
        uOpacity: { value: 0.92 },
        uPointSize: { value: 0.5 },
        uPointScale: { value: 360 },
      },
      vertexShader: SNOW_VERTEX,
      fragmentShader: SNOW_FRAGMENT,
      transparent: true,
      depthWrite: false,
      fog: false,
    });
    this.snow = new THREE.Points(snowData.geometry, this.snowMaterial);
    this.snow.frustumCulled = false;
    this.snow.renderOrder = 6;
    this.snow.visible = false;
    scene.add(this.rain, this.snow);
  }

  /** Lighting is a cheap shared sky/direct approximation; no per-drop shadows. */
  setEnvironment(surface: WeatherSurface, field: WeatherField, cloudBase: number, light: THREE.Color): void {
    for (const material of [this.rainMaterial, this.snowMaterial]) {
      Object.assign(material.uniforms, surface.uniforms, field.uniforms);
      material.uniforms.uWeatherOn.value = 1;
      material.uniforms.uCloudBase.value = cloudBase;
      material.uniforms.uColor.value.copy(light);
    }
    this.snowPartner?.setEnvironment(surface, field, cloudBase, light);
  }

  update(
    dt: number,
    playerWorld: THREE.Vector3,
    origin: THREE.Vector3,
    pose: FxPose | null,
    w: WeatherColumn,
    groundDrop = GROUND_DROP,
    camWorld?: THREE.Vector3,
  ): void {
    if (this.snowPartner) {
      const f = w.snowFraction;
      Object.assign(this.mixedWeather, w, { snowFraction: undefined, kind: f === undefined ? 0 : 2, precip: f === undefined ? 0 : w.precip * f });
      this.snowPartner.update(dt, playerWorld, origin, pose, this.mixedWeather, groundDrop, camWorld);
      if (f !== undefined) {
        Object.assign(this.mixedWeather, w, { snowFraction: undefined, kind: 1, precip: w.precip * (1 - f) });
        w = this.mixedWeather;
      }
    }
    this.clock += dt;
    const rawKind = w.kind;
    const target = pose && rawKind !== 0 ? Math.min(1, w.precip) : 0;
    this.intensity += (target - this.intensity) * (1 - Math.exp(-2 * dt));
    if (rawKind !== 0) this.lastKind = rawKind;
    const kind = rawKind !== 0 ? rawKind : this.lastKind;
    if (!pose || this.intensity < 0.01) {
      this.rain.visible = false;
      this.snow.visible = false;
      this.wasActive = false;
      return;
    }

    this._pLocal.copy(playerWorld).sub(pose.center).applyQuaternion(pose.invQuat);
    this._upLocal.copy(this._pLocal).normalize();
    this._tanA.set(Math.abs(this._upLocal.x) > 0.9 ? 0 : 1, Math.abs(this._upLocal.x) > 0.9 ? 1 : 0, 0);
    this._tanA.crossVectors(this._upLocal, this._tanA).normalize();
    this._tanB.crossVectors(this._upLocal, this._tanA).normalize();
    this._cLocal.copy(camWorld ?? playerWorld).sub(pose.center).applyQuaternion(pose.invQuat);
    const camDist = this._cLocal.distanceTo(this._pLocal);
    const boxR = Math.min(BOX_R_MAX, BOX_R + camDist * BOX_R_CAM);
    this._cLocal.lerp(this._pLocal, CENTER_LERP);
    const hC = (this._cLocal.x - this._pLocal.x) * this._upLocal.x
      + (this._cLocal.y - this._pLocal.y) * this._upLocal.y
      + (this._cLocal.z - this._pLocal.z) * this._upLocal.z;
    const drop = Math.min(MAX_DROP, Math.max(GROUND_DROP, groundDrop));

    const teleported = this.wasActive && this._pLocal.distanceToSquared(this._lastPlayerLocal) > 200 * 200;
    const faceInvalid = this.faceAxis >= 0 && this.faceComponent(this._upLocal) * this.faceSign < 0.35;
    if (!this.wasActive || kind !== this.activeKind || teleported || faceInvalid) this.resetField(kind);
    this.wasActive = true;
    this.activeKind = kind;
    this._lastPlayerLocal.copy(this._pLocal);

    const gust = kind === 2 ? 0.35 + 0.65 * (0.5 + 0.5 * Math.sin(this.clock * 0.15 + 1.7)) : 1;
    const windK = (kind === 2 ? WIND_SNOW : WIND_RAIN) * gust;
    this._windLocal.set(w.windx, w.windy, w.windz).multiplyScalar(w.windSpeed * windK);
    const fall = kind === 2 ? SNOW_FALL : RAIN_FALL;
    this.uniforms.uWindPath.value.addScaledVector(this._windLocal, dt);
    this.uniforms.uFallDistance.value += fall * dt;

    const u = this.uniforms;
    u.uCenter.value.copy(this._cLocal);
    u.uBasisA.value.copy(this._tanA);
    u.uBasisB.value.copy(this._tanB);
    u.uUp.value.copy(this._upLocal);
    u.uHC.value = hC;
    u.uBoxR.value = boxR;
    u.uDrop.value = drop;
    u.uIntensity.value = Math.min(1, this.intensity);
    u.uTime.value = this.clock;
    this._sourceLocal.copy(this._cLocal).sub(u.uWindPath.value);
    u.uGridBase.value.set(
      Math.floor(this._sourceLocal.dot(u.uGridA.value) / CELL_SIZE) - Math.floor(GRID_N / 2),
      Math.floor(this._sourceLocal.dot(u.uGridB.value) / CELL_SIZE) - Math.floor(GRID_N / 2),
    );

    this.rain.position.copy(pose.center).sub(origin);
    this.snow.position.copy(this.rain.position);
    this.rain.quaternion.copy(pose.quat);
    this.snow.quaternion.copy(pose.quat);
    if (kind === 2) {
      this.snowMaterial.uniforms.uOpacity.value = 0.92 * Math.min(1, this.intensity * 1.5);
      const viewportHeight = typeof window === "undefined" ? 720 : window.innerHeight * window.devicePixelRatio;
      this.snowMaterial.uniforms.uPointScale.value = viewportHeight * 0.5;
      this.snow.visible = true;
      this.rain.visible = false;
    } else {
      this.rainMaterial.uniforms.uOpacity.value = 0.5 * Math.min(1, this.intensity * 1.5);
      this._vel.copy(this._upLocal).multiplyScalar(-fall).add(this._windLocal);
      this._streak.copy(this._vel).multiplyScalar(RAIN_STREAK / Math.max(1e-3, this._vel.length()));
      this.rainMaterial.uniforms.uStreak.value.copy(this._streak);
      this.rain.visible = true;
      this.snow.visible = false;
    }
  }

  private faceComponent(v: THREE.Vector3): number {
    return this.faceAxis === 0 ? v.x : this.faceAxis === 1 ? v.y : v.z;
  }

  private resetField(kind: number): void {
    const ax = Math.abs(this._upLocal.x), ay = Math.abs(this._upLocal.y), az = Math.abs(this._upLocal.z);
    this.faceAxis = ax >= ay && ax >= az ? 0 : ay >= az ? 1 : 2;
    this.faceSign = this.faceComponent(this._upLocal) < 0 ? -1 : 1;
    const u = this.uniforms;
    if (this.faceAxis === 0) {
      u.uGridA.value.set(0, 1, 0); u.uGridB.value.set(0, 0, 1); u.uFace.value.set(this.faceSign, 0, 0);
    } else if (this.faceAxis === 1) {
      u.uGridA.value.set(1, 0, 0); u.uGridB.value.set(0, 0, 1); u.uFace.value.set(0, this.faceSign, 0);
    } else {
      u.uGridA.value.set(1, 0, 0); u.uGridB.value.set(0, 1, 0); u.uFace.value.set(0, 0, this.faceSign);
    }
    u.uFaceSeed.value = this.faceAxis * 2 + (this.faceSign < 0 ? 1 : 0);
    u.uShellRadius.value = this._pLocal.length();
    u.uWindPath.value.set(0, 0, 0);
    u.uFallDistance.value = 0;
    this.activeKind = kind;
  }

  private particleSeed(index: number): [number, number, number, number, number, number] {
    const o = Math.max(0, Math.min(N - 1, index | 0)) * 3;
    const cellX = this.uniforms.uGridBase.value.x + this.cells[o];
    const cellY = this.uniforms.uGridBase.value.y + this.cells[o + 1];
    const ordinal = this.cells[o + 2] + this.uniforms.uFaceSeed.value * 31;
    return [
      cellX,
      cellY,
      hash31(cellX, cellY, ordinal),
      hash31(cellX + 19.19, cellY + 7.13, ordinal + 3.71),
      hash31(cellX + 3.17, cellY + 29.41, ordinal + 11.73),
      hash31(cellX + 43.11, cellY + 5.23, ordinal + 17.17),
    ];
  }

  /** Stable absolute identity for comparing overlapping diagnostic samples across grid shifts. */
  particleCellKey(index: number): string {
    const o = Math.max(0, Math.min(N - 1, index | 0)) * 3;
    return `${this.uniforms.uFaceSeed.value}:${this.uniforms.uGridBase.value.x + this.cells[o]}:${this.uniforms.uGridBase.value.y + this.cells[o + 1]}:${this.cells[o + 2]}`;
  }

  /** True when the shader keeps this slot inside the current coverage/intensity mask. */
  particleVisible(index: number): boolean {
    const [cellX, cellY, sx, sy, sz, activity] = this.particleSeed(index);
    if (activity > this.uniforms.uIntensity.value) return false;
    const coordA = (cellX + sx) * CELL_SIZE;
    const coordB = (cellY + sy) * CELL_SIZE;
    const faceCoord = Math.sqrt(Math.max(0, this.uniforms.uShellRadius.value ** 2 - coordA ** 2 - coordB ** 2));
    this._vel.copy(this.uniforms.uGridA.value).multiplyScalar(coordA)
      .addScaledVector(this.uniforms.uGridB.value, coordB)
      .addScaledVector(this.uniforms.uFace.value, faceCoord);
    this._sampleUp.copy(this._vel).normalize();
    this._vel.addScaledVector(this._sampleUp, THREE.MathUtils.lerp(-this.uniforms.uDrop.value, BOX_TOP, sz))
      .add(this.uniforms.uWindPath.value)
      .sub(this.uniforms.uCenter.value);
    return Math.abs(this._vel.dot(this.uniforms.uBasisA.value)) <= this.uniforms.uBoxR.value
      && Math.abs(this._vel.dot(this.uniforms.uBasisB.value)) <= this.uniforms.uBoxR.value;
  }

  /** CPU mirror of the vertex placement, intended only for diagnostics/tests. */
  sampleLocalParticle(index: number, out: THREE.Vector3, snow = this.snow.visible): THREE.Vector3 {
    const [cellX, cellY, sx, sy, sz, phase] = this.particleSeed(index);
    const u = this.uniforms;
    const coordA = (cellX + sx) * CELL_SIZE;
    const coordB = (cellY + sy) * CELL_SIZE;
    const faceCoord = Math.sqrt(Math.max(0, u.uShellRadius.value ** 2 - coordA ** 2 - coordB ** 2));
    out.copy(u.uGridA.value).multiplyScalar(coordA)
      .addScaledVector(u.uGridB.value, coordB)
      .addScaledVector(u.uFace.value, faceCoord);
    this._sampleUp.copy(out).normalize();
    out.addScaledVector(this._sampleUp, THREE.MathUtils.lerp(-u.uDrop.value, BOX_TOP, sz))
      .add(u.uWindPath.value);
    const baseH = this._sampleUp.copy(out).sub(u.uCenter.value).dot(u.uUp.value) + u.uHC.value;
    const fallMul = snow ? 0.75 + 0.5 * phase : 1;
    const span = BOX_TOP + u.uDrop.value;
    const fallingH = baseH - u.uFallDistance.value * fallMul;
    const h = fallingH - span * Math.floor((fallingH + u.uDrop.value) / span);
    out.addScaledVector(u.uUp.value, h - baseH);
    if (snow) {
      const flutterPhase = phase * Math.PI * 2 + u.uTime.value * (1.1 + phase * 0.6);
      out.addScaledVector(u.uBasisA.value, Math.sin(flutterPhase) * SNOW_FLUTTER)
        .addScaledVector(u.uBasisB.value, Math.cos(flutterPhase * 0.73) * SNOW_FLUTTER);
    }
    return out;
  }

  dispose(): void {
    this.snowPartner?.dispose();
    this.rain.removeFromParent();
    this.snow.removeFromParent();
    this.rain.geometry.dispose();
    this.rainMaterial.dispose();
    this.snow.geometry.dispose();
    this.snowMap.dispose();
    this.snowMaterial.dispose();
  }
}
